
import { useCallback, useMemo } from 'react';

/**
 * Hook to check if a path is public (doesn't require authentication)
 * Optimized to reduce redundant checks and console logs
 */
export const usePublicPaths = () => {
  // Memoize the public paths array
  const publicPaths = useMemo(() => [
    '/',
    '/index',
    '/signup',
    '/signin',
    '/login', 
    '/auth',
    '/auth/callback',
    '/verify-email',
    '/check-email',
    '/forgot-password',
    '/reset-password',
    '/invitation',
    '/contact-action-confirmation',
    '/goodbye',
    '/black',
    '/terms',
    '/privacy',
    '/faq',
    '/learn-more',
    '/legal',
  ], []);

  // Create a prefix map for faster lookups
  const publicPathPrefixes = useMemo(() => [
    '/invited-user-signup/',
    '/invitation/',
    '/contact-approval/',
    '/d/',
  ], []);

  // Cache for path check results
  const pathCheckCache = useMemo(() => new Map<string, boolean>(), []);

  /**
   * Check if a path is public with optimized caching
   */
  const isPublicPath = useCallback((path: string): boolean => {
    // Check cache first to avoid redundant processing
    if (pathCheckCache.has(path)) {
      return pathCheckCache.get(path)!;
    }
    
    // 1. Check direct matches first (most common case)
    if (publicPaths.includes(path)) {
      pathCheckCache.set(path, true);
      return true;
    }
    
    // 2. Check prefix matches (for paths with parameters)
    for (const prefix of publicPathPrefixes) {
      if (path.startsWith(prefix)) {
        pathCheckCache.set(path, true);
        return true;
      }
    }
    
    // 3. Check other public paths that might be prefixes
    for (const publicPath of publicPaths) {
      if (path.startsWith(publicPath + '/')) {
        pathCheckCache.set(path, true);
        return true;
      }
    }
    
    // Not a public path
    pathCheckCache.set(path, false);
    return false;
  }, [publicPaths, publicPathPrefixes, pathCheckCache]);

  return isPublicPath;
};
